import { PriceValue } from '@ca/common/types'
import { debugOrder } from '@order/utils/getDebugLog'
import isEqual from 'lodash/isEqual'
import { create } from 'zustand'
import { devtools } from 'utils/devtools'
import { immer } from 'zustand/middleware/immer'
import { SelectedPackage } from '../orderTypes'
import {
  discountsSlice,
  initialDiscountState,
} from './state/orderDiscountsSlice'
import { orderItemsSlice } from './state/orderItemsSlice'
import orderRequirementsSlice, {
  initialRequirementsState,
} from './state/orderRequirementsSlice'
import {
  FinalOrderState,
  initialOrderState,
  PdOverrideValues,
} from './state/orderState'
import { RefractionUtils } from '@ca/common'

export const useOrderState = create<FinalOrderState>()(
  devtools(
    immer((set, get, store) => ({
      result: null,
      order: initialOrderState,
      discounts: initialDiscountState,
      steps: {},
      setUseInvoice: (value) =>
        set((state) => {
          state.order.invoice = value
        }),

      completeItem: (itemId, states, bundleType) =>
        set(
          (state) => {
            const item = state.order.items.find((item) => item.id === itemId)
            if (!item) {
              return
            }
            const { bundle, ...rest } = states

            state.steps[itemId] = rest
            if (bundle) {
              state.order.bundles[bundleType] = {
                type: bundleType,
                steps: bundle,
              }
            }
            item.isCompleted = true
          },
          undefined,
          'completeItem'
        ),
      updateItem: (itemId, states, bundleType) =>
        set(
          (state) => {
            const item = state.order.items.find((item) => item.id === itemId)
            if (!item) {
              return
            }
            const { bundle, ...rest } = states
            const currentSteps = state.steps[itemId]
            const currentBundle = state.order.bundles[bundleType]
            const nextBundle = bundle
              ? {
                  type: bundleType,
                  steps: bundle,
                }
              : undefined

            // Avoid unnecessary store writes while the screen is focused.
            const isStepsSame = isEqual(currentSteps, rest)
            const isBundleSame = bundle
              ? isEqual(currentBundle, nextBundle)
              : true
            if (isStepsSame && isBundleSame) {
              return
            }

            state.steps[itemId] = rest
            if (bundle) {
              state.order.bundles[bundleType] = nextBundle
            }

            return state
          },
          undefined,
          'updateItem'
        ),
      reset: () =>
        set((state) => {
          state.order = initialOrderState
          state.result = null
          // state.order.flowName = flowName
          // state.payment = null
          state.requirements = initialRequirementsState
          state.discounts = initialDiscountState
        }),
      setResult: (result) =>
        set(
          (state) => {
            state.result = result
          },
          undefined,
          'setResult'
        ),
      applyDiscountedOrderPrices: (orders) =>
        set(
          (state) => {
            if (!state.result || !orders.length) {
              return
            }
            const byId = new Map(
              orders.map((order) => [order.id, order.amount])
            )
            state.result.orders.forEach((order) => {
              const finalPrice = byId.get(order.id)
              if (finalPrice === undefined) {
                return
              }
              order.final_price = finalPrice
            })
          },
          undefined,
          'applyDiscountedOrderPrices'
        ),

      clearFinalPrices: (orderIds) =>
        set(
          (state) => {
            if (!state.result || !orderIds.length) {
              return
            }
            const ids = new Set(orderIds)
            state.result.orders.forEach((order) => {
              if (!ids.has(order.id)) {
                return
              }
              order.final_price = undefined
            })
          },
          undefined,
          'clearFinalPrices'
        ),

      invalidateResult: () =>
        set((state) => {
          debugOrder.log('invalidateResult', state.result)
          if (state.result) {
            state.result.isValid = false
          }
        }),
      setPackage: (
        item,
        { variant, region, frames, bundle = 'base', overrides }
      ) => {
        const selected = {
          id: item.id,
          name: item.name,
          lensPrefix: variant?.lensPrefix || item.lensPrefix,
          type: item.type,
          value: variant?.value || item.value,
          variantId: variant?.id,
          frames,
          overrides,
          color: variant?.color || item.color,
          price: PriceValue.safe([variant?.price, item?.price], region),
        } satisfies SelectedPackage

        set((state) => {
          state.order.packageCfg[bundle] = selected
        })
        return selected
      },
      updateFrame: (frameId, { measurement, shape }) => {
        set((state) => {
          const items = state.order.items.filter((i) => i.frame?.id === frameId)
          items.forEach((item) => {
            if (!item.frame) {
              return
            }
            if (measurement) {
              item.frame.measurement = measurement
            }
            if (shape) {
              item.frame.shape = shape
            }
          })
        })
      },
      updatePd: (pd, type) =>
        set((s) => {
          const currentType = s.order.pd.current
          const currentValue = s.order.pd.values[type]

          // Avoid noisy updates during PD tab/value sync.
          if (
            currentType === type &&
            RefractionUtils.pd.isSame(currentValue, pd)
          ) {
            return
          }

          s.order.pd.values[type] = pd
          s.order.pd.current = type
        }),

      setPrescriptionId: (prescriptionId) =>
        set((s) => {
          if (s.order.prescriptionId === prescriptionId) {
            return
          }
          s.order.prescriptionId = prescriptionId
        }),

      addPromotion: (promotion) =>
        set((state) => {
          state.order.promotions = [...state.order.promotions, promotion]
        }),
      setComment: (comment) =>
        set((state) => {
          state.order.comment = comment
        }),

      ...orderItemsSlice(set, get, store),
      ...orderRequirementsSlice(set, get, store),
      ...discountsSlice(set, get, store),
    })),
    {
      name: 'order',
    }
  )
)

export const useAddFrame = () => useOrderState((state) => state.addItem)

export const useOrder = () => {
  return useOrderState((s) => s.order)
}
